<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
  size: { type: Number, default: 40 },
  showText: { type: Boolean, default: true },
  collapsed: { type: Boolean, default: false },
});

const animated = ref(false);
onMounted(() => {
  requestAnimationFrame(() => { animated.value = true; });
});
</script>

<template>
  <div class="enny-logo" :class="{ animated }">
    <svg
      viewBox="0 0 1024 1024"
      :width="size"
      :height="size"
      class="enny-logo-svg"
    >
      <defs>
        <linearGradient id="ctGreen" x1="0" y1="0" x2=".65" y2="1">
          <stop offset="0" stop-color="#4dff9e" />
          <stop offset=".55" stop-color="#22e07a" />
          <stop offset="1" stop-color="#0aa855" />
        </linearGradient>
        <linearGradient id="ctGold" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stop-color="#f79c1c" />
          <stop offset=".5" stop-color="#fdb827" />
          <stop offset="1" stop-color="#ffd75e" />
        </linearGradient>
        <filter id="goo" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="18" result="b" />
          <feColorMatrix
            in="b"
            mode="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -10"
            result="g"
          />
          <feComposite in="SourceGraphic" in2="g" operator="atop" />
        </filter>
      </defs>

      <!-- Arc -->
      <path
        d="M600 200 A350 350 0 1 0 810 600"
        fill="none"
        stroke="url(#ctGreen)"
        stroke-width="240"
        stroke-linecap="round"
        class="enny-arc"
      />

      <!-- Goo dots -->
      <g filter="url(#goo)">
        <circle cx="470" cy="510" r="180" fill="#fbbf24" class="enny-dot-in" />
        <line
          x1="470" y1="510" x2="916" y2="272"
          stroke="#fbbf24"
          stroke-width="108"
          stroke-linecap="round"
          class="enny-flame"
        />
        <circle cx="916" cy="272" r="175" fill="#fbbf24" class="enny-dot-out" />
      </g>
    </svg>

    <!-- Text -->
    <span
      v-if="showText && !collapsed"
      class="enny-text"
    >
      <span
        v-for="(ch, i) in [...'Enny']"
        :key="'e' + i"
        class="enny-letter enny-bold"
        :style="{ animationDelay: `${1.35 + i * 0.045}s` }"
      >{{ ch }}</span>
      <span
        v-for="(ch, i) in [...'Chat']"
        :key="'c' + i"
        class="enny-letter enny-semi"
        :style="{ animationDelay: `${1.35 + (4 + i) * 0.045}s` }"
      >{{ ch }}</span>
    </span>
  </div>
</template>

<style>
.enny-logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* ── Arc animation ── */
.enny-arc {
  stroke-dasharray: 2200;
  stroke-dashoffset: 2200;
  transition: none;
}
.animated .enny-arc {
  animation: drawArc 1s cubic-bezier(0.65, 0, 0.35, 1) forwards;
}

@keyframes drawArc {
  to { stroke-dashoffset: 0; }
}

/* ── Dots animation ── */
.enny-dot-in,
.enny-dot-out,
.enny-flame {
  transform-origin: center;
  transform-box: fill-box;
  opacity: 0;
  transform: scale(0);
}

.animated .enny-dot-in {
  animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.9s forwards;
}
.animated .enny-flame {
  animation: popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.85s forwards;
}
.animated .enny-dot-out {
  animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 1.25s forwards;
}

@keyframes popIn {
  0% { opacity: 0; transform: scale(0.4); }
  70% { transform: scale(1.1); }
  100% { opacity: 1; transform: scale(1); }
}

/* ── Text animation ── */
.enny-text {
  display: flex;
  font-size: 1.35rem;
  line-height: 1;
  color: #f59e0b !important;
  font-family: 'Nunito', 'Inter', sans-serif;
}

.enny-letter {
  display: inline-block;
  opacity: 0;
  transform: translateY(14px);
}
.animated .enny-letter {
  animation: slideUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.enny-bold { font-weight: 900; }
.enny-semi { font-weight: 600; }

@keyframes slideUp {
  to { opacity: 1; transform: translateY(0); }
}
</style>
